import {
  BellAlertIcon,
  BoltIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentCheckIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from '@heroicons/react/20/solid';
import { useBrandConfig } from '~/config/branding';

const medCredVaultFeatures = [
  {
    name: 'Real-Time Credential Verification',
    description:
      'Instantly access live provider credentials with trust-tiered risk scoring (Green/Gold Check), ensuring you only hire fully qualified professionals.',
    icon: ShieldCheckIcon,
  },
  {
    name: 'Auto-Prefilled Credentialing Packets',
    description:
      'AI prepopulates your facility-specific onboarding forms, eliminating administrative bottlenecks and reducing time-to-hire from weeks to hours.',
    icon: DocumentCheckIcon,
  },
  {
    name: 'Continuous Compliance Monitoring',
    description:
      'Receive real-time alerts for expiring licenses, sanctions, or missing documents. Prevent compliance issues before they become problems.',
    icon: BellAlertIcon,
  },
  {
    name: 'Board-Ready Credential Packets',
    description:
      'Access comprehensive, pre-verified credential packages that meet regulatory standards and facility requirements instantly.',
    icon: DocumentCheckIcon,
  },
  {
    name: 'AI-Powered Risk Scoring',
    description:
      'Advanced algorithms analyze provider credentials across 20,000+ data points to ensure only job-ready candidates match your needs.',
    icon: ChartBarIcon,
  },
  {
    name: 'Reduced Administrative Burden',
    description:
      'Eliminate redundant paperwork, manual verification, and credential chasing. Focus your team on patient care, not paperwork.',
    icon: ClockIcon,
  },
];

const healthcareLxFeatures = [
  {
    name: 'Slash Hiring Costs by 50-75%',
    description:
      'Eliminate costly agency fees and markups. Direct hiring for temporary staffing saves 50-75%, permanent hiring saves 60-90%.',
    icon: CurrencyDollarIcon,
  },
  {
    name: 'AI-Powered Provider Matching',
    description:
      'Instantly find pre-vetted, available providers based on specialty, location, credentials, and availability using advanced AI algorithms.',
    icon: BoltIcon,
  },
  {
    name: 'Fill Shifts in Real-Time',
    description:
      'VRBO-style interactive calendar management lets you post shifts and get matched with available, credentialed providers instantly.',
    icon: ClockIcon,
  },
  {
    name: 'Flexible Engagement Models',
    description:
      'Choose from fixed rates, auctions, or best-offer structures to control costs and optimize your staffing budget with full transparency.',
    icon: CurrencyDollarIcon,
  },
  {
    name: 'Direct Access to Providers',
    description:
      'Connect instantly with credentialed professionals without third-party intermediaries. Post opportunities for $10 per listing for 30 days, or $199/month for up to 50 active listings.',
    icon: UserGroupIcon,
  },
  {
    name: 'Healthcare Labor as a Service (B2B)',
    description:
      'Hire entire teams or provider groups for complex staffing needs. Scalable solutions for anesthesia groups, hospitalist teams, and more.',
    icon: BuildingOfficeIcon,
  },
  {
    name: 'Workforce Intelligence & Analytics',
    description:
      'AI-powered insights predict staffing shortages, wage trends, and market demand. Make data-driven hiring decisions with real-time analytics.',
    icon: ChartBarIcon,
  },
  {
    name: 'Only Hire Compliant Providers',
    description:
      'AI compliance gating ensures only fully licensed, credentialed providers can apply. Reduce risk and protect your facility.',
    icon: ShieldCheckIcon,
  },
  {
    name: 'Faster Time-to-Fill',
    description:
      'Post a job and get matched with available providers in minutes, not weeks. Pre-verified credentials mean instant onboarding readiness.',
    icon: ClockIcon,
  },
];

// [MCV-1933] HLX launch pricing, shown on the HLX organizations page only.
export const HLX_PRICING_DISCLOSURE =
  "New business customers are eligible for $50,000 in promotional credits toward HLX platform and placement fees, expiring 18 months after signup. Credits cover up to 50% of each fee invoice, don't apply to commitments or listing fees, are nontransferable and have no cash value.";

export const hlxPricing = [
  {
    name: 'Listings',
    description:
      '$10 per listing for 30 days, charged when it goes live and renewed while it stays active. Enterprise: $199/month for up to 50 active listings, $10 each beyond that.',
  },
  {
    name: 'Healthcare labor as a service (B2B)',
    description:
      'A commitment of 0.10% of first-year contract value ($500 minimum, $10,000 maximum) when both organizations accept. A 1.5% platform fee is billed monthly for the first 12 months, with the commitment credited against it.',
  },
  {
    name: 'Locum tenens and temporary staffing',
    description:
      'A commitment per accepted engagement: $250 MD/DO · $150 APP · $75 nurse · $50 allied. A 3% platform fee is billed monthly for up to 12 months, with the commitment credited against it.',
  },
  {
    name: 'Permanent placement',
    description:
      "A placement fee of $5,000 MD/DO · $7,000 APP · $5,000 nurse · $2,000 allied. The commitment is charged when the position is accepted and credited against the fee. The balance is due on the provider's start date, and isn't charged if the provider never starts.",
  },
  {
    name: 'MedCredVault and provider profiles',
    description: 'Basic MedCredVault and provider profiles are free.',
  },
];

export const Benefits = () => {
  // [MCV-1490] Each brand's public site shows only its own section — the MCV
  // site drops the HealthcareLX block (and its marketplace "at a glance"
  // takeaways), the HLX site drops the MedCredVault block.
  const { brand } = useBrandConfig();
  return (
    <div className="bg-white">
      {/* MedCredVault Section */}
      {brand === 'mcv' && (
        <div className="py-10 sm:py-32">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl">
              <p className="text-base/7 font-semibold text-primary-50 text-left">
                For Organizations
              </p>
              <h2 className="mt-2 text-4xl font-semibold tracking-tight text-left text-pretty text-grey-100 sm:text-5xl">
                MedCredVault
              </h2>
              <p className="mt-6 text-lg/8 text-grey-300 text-left">
                Ensure you only hire fully credentialed, compliant providers through real-time AI
                verification. Eliminate administrative bottlenecks and reduce compliance risks.
              </p>
            </div>
            <dl className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 text-base/7 text-grey-300 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-x-16">
              {medCredVaultFeatures.map((feature) => (
                <div key={feature.name} className="relative pl-9">
                  <dt className="inline font-semibold text-grey-100">
                    <feature.icon
                      aria-hidden="true"
                      className="absolute top-1 left-1 size-5 text-primary-50"
                    />
                    {feature.name}
                  </dt>{' '}
                  <dd className="inline">{feature.description}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}

      {/* HealthcareLX Section */}
      {brand === 'hlx' && (
        <>
          <div className="py-10 sm:py-32">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="mx-auto max-w-2xl">
                <p className="text-base/7 font-semibold text-primary-50 text-left">
                  For Organizations
                </p>
                <h2 className="mt-2 text-4xl font-semibold tracking-tight text-left text-pretty text-grey-100 sm:text-5xl">
                  HealthcareLX
                </h2>
                <p className="mt-6 text-lg/8 text-grey-300 text-left">
                  Hire smarter, faster, and more cost-effectively. Our AI-driven marketplace
                  connects you directly with credentialed providers, eliminating agency fees and
                  delays.
                </p>
              </div>
              <dl className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 text-base/7 text-grey-300 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-x-16">
                {healthcareLxFeatures.map((feature) => (
                  <div key={feature.name} className="relative pl-9">
                    <dt className="inline font-semibold text-grey-100">
                      <feature.icon
                        aria-hidden="true"
                        className="absolute top-1 left-1 size-5 text-good"
                      />
                      {feature.name}
                    </dt>{' '}
                    <dd className="inline">{feature.description}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          {/* [MCV-1933] Launch pricing */}
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-10 sm:pb-16">
            <div className="border-t border-grey-800 mb-16"></div>
            <div className="mx-auto max-w-2xl text-left">
              <h2
                className="text-3xl font-semibold tracking-tight text-grey-100 sm:text-4xl"
                data-cy="hlx-pricing-heading"
              >
                Launch Pricing
              </h2>
              <p className="mt-6 text-lg/8 text-grey-300">
                Listings are charged when they go live. When an engagement is accepted you pay a
                commitment, which is credited against the fees that follow.
              </p>
            </div>
            <dl
              className="mx-auto mt-12 grid max-w-2xl grid-cols-1 gap-8 text-base/7 text-grey-300 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-x-16"
              data-cy="hlx-pricing"
            >
              {hlxPricing.map((item) => (
                <div key={item.name} className="relative pl-9 text-left">
                  <dt className="inline font-semibold text-grey-100">
                    <CurrencyDollarIcon
                      aria-hidden="true"
                      className="absolute top-1 left-1 size-5 text-good"
                    />
                    {item.name}
                  </dt>{' '}
                  <dd className="inline">{item.description}</dd>
                </div>
              ))}
            </dl>
            <p
              className="mx-auto mt-12 max-w-2xl text-left text-base/7 text-grey-300 lg:mx-0 lg:max-w-none"
              data-cy="hlx-pricing-disclosure"
            >
              {HLX_PRICING_DISCLOSURE}
            </p>
          </div>

          {/* Key Takeaways Section */}
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-10 sm:pb-24">
            <div className="border-t border-grey-800 mb-16"></div>
            <div className="mx-auto max-w-2xl text-left">
              {/* [MCV-1512] `h2`, not `h3`: a page-level benefits summary that
                  sits as a sibling of the product section above, not a
                  subsection of it. The size stays `text-3xl/4xl` — the level is
                  semantic, the scale is the design. */}
              <h2 className="text-3xl font-semibold tracking-tight text-grey-100 sm:text-4xl mb-8">
                Your Benefits at a Glance
              </h2>
              <div className="space-y-4 text-lg text-grey-300">
                <p className="flex items-start gap-3">
                  <span className="text-good text-2xl">✓</span>
                  <span>
                    <strong>Reduce Hiring Costs by 50-75%</strong> – Cut agency fees, hire directly,
                    and pay only for successful hires.
                  </span>
                </p>
                <p className="flex items-start gap-3">
                  <span className="text-good text-2xl">✓</span>
                  <span>
                    <strong>Faster, More Reliable Staffing</strong> – AI-matched providers ready to
                    work in real-time.
                  </span>
                </p>
                <p className="flex items-start gap-3">
                  <span className="text-good text-2xl">✓</span>
                  <span>
                    <strong>Fully Credentialed, Compliant Workforce</strong> – AI ensures only
                    pre-verified candidates are visible.
                  </span>
                </p>
                <p className="flex items-start gap-3">
                  <span className="text-good text-2xl">✓</span>
                  <span>
                    <strong>Flexible Staffing Solutions</strong> – Fill shifts, contract groups, or
                    hire full-time employees with ease.
                  </span>
                </p>
                <p className="flex items-start gap-3">
                  <span className="text-good text-2xl">✓</span>
                  <span>
                    <strong>Workforce Intelligence & AI Insights</strong> – Track shortages,
                    optimize hiring budgets, and stay ahead of market trends.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
